import { scaleOpacity } from '@dynamic/ui';
import styled from 'styled-components';

import { MEDIA_QUERIES } from '#/hooks/useResponsiveValue';
import { BreakpointKeys, horizontalDirection } from '#/service/constants';
import { remify } from '#/service/utils';

import { PROMOTIONAL_LABEL_HEIGHT_PX } from './ProductCard.styled';

const SLIDER_GUTTER_PX = 50;

export const SliderContainer = styled.div`
  position: relative;
  max-width: 100vw;
  margin: 0 0 ${remify(48)} 0;
`;

interface SliderWrapperProps {
  $showArrows: boolean;
  $hasPromotionalLabel: boolean;
}

export const SliderWrapper = styled.div<SliderWrapperProps>`
  display: flex;
  align-items: stretch;
  justify-content: ${({ $showArrows }) =>
    $showArrows ? 'flex-start' : 'center'};
  overflow-x: ${({ $showArrows }) => ($showArrows ? 'auto' : 'visible')};
  scroll-behavior: smooth;
  scroll-snap-type: ${({ $showArrows }) =>
    $showArrows ? 'x mandatory' : 'none'};
  gap: ${remify(32)};
  padding: ${({ $hasPromotionalLabel, $showArrows }) => {
    const topPadding = remify(
      $hasPromotionalLabel ? PROMOTIONAL_LABEL_HEIGHT_PX : 0
    );

    return $showArrows
      ? `${topPadding} ${remify(SLIDER_GUTTER_PX)} 0 ${remify(SLIDER_GUTTER_PX)}`
      : `${topPadding} 0 0 0`;
  }};
  scroll-padding: ${({ $showArrows }) =>
    $showArrows
      ? `0 ${remify(SLIDER_GUTTER_PX)} 0 ${remify(SLIDER_GUTTER_PX)}`
      : '0'};
  scrollbar-width: none;
  max-width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  @media ${MEDIA_QUERIES[BreakpointKeys.SM]} {
    flex-direction: column;
    overflow-x: visible;
    scroll-snap-type: none;
    justify-content: flex-start;
    padding: ${remify(8)} 0;
  }
`;

export const CardWrapper = styled.div<{ $hasPromotionalLabel: boolean }>`
  flex: 0 0 auto;
  scroll-snap-align: start;
  width: ${remify(512)};
  max-width: calc((100vw - ${remify(120)}) / 2.5);
  display: flex;

  @media ${MEDIA_QUERIES[BreakpointKeys.SM]} {
    width: 100%;
    max-width: 100%;
    scroll-snap-align: none;
    margin-top: ${({ $hasPromotionalLabel }) =>
      $hasPromotionalLabel ? remify(PROMOTIONAL_LABEL_HEIGHT_PX) : 0};
  }
`;

interface ArrowButtonProps {
  $direction: horizontalDirection;
  $hasPromotionalLabel: boolean;
}

export const ArrowButton = styled.button<ArrowButtonProps>`
  position: absolute;
  top: ${({ $hasPromotionalLabel }) =>
    $hasPromotionalLabel ? remify(PROMOTIONAL_LABEL_HEIGHT_PX) : 0};
  bottom: 0;
  ${({ $direction }) =>
    $direction === horizontalDirection.LEFT ? 'left: 0;' : 'right: 0;'}
  z-index: 10;
  width: ${remify(47)};
  border: none;
  background: ${({ theme, $direction }) =>
    `linear-gradient(${$direction === horizontalDirection.LEFT ? '90deg' : '270deg'}, ${scaleOpacity(theme?.color?.pageBackground, 0.5)}, transparent)`};
  color: var(--iapForeground);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0;
    cursor: not-allowed;
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  @media ${MEDIA_QUERIES[BreakpointKeys.SM]} {
    display: none;
  }
`;

export const DividerWrapper = styled.div<{ $totalCards: number }>`
  width: ${({ $totalCards }) =>
    $totalCards <= 2 ? 'calc(100% + 16px)' : '100%'};
  margin-left: ${({ $totalCards }) => ($totalCards <= 2 ? '-8px' : '0')};

  @media ${MEDIA_QUERIES[BreakpointKeys.SM]} {
    width: 100%;
    margin-left: 0;
  }
`;
